import { useCallback, useEffect, useState } from 'react'

import { useFetchApiObject } from '@e3dc-react/shell/Hooks/useFetch'
import { useUserHelpers } from 'src/Hooks'
import {
  useCompanyErrorNotification,
  useCompanyUsers,
  CompanyInstallerTableItemModel,
  CompanyInvitationModel,
  CompanySettingsUserDataModel,
} from 'src/Libs/company-settings'
import { useTypedSelector } from 'src/State/RootReducer'
import { UserData } from 'src/State/User/UserModel'

interface useCompanyInstallerHelperHook {
  handleChangeNotifications: (value: boolean, companyId: number, userId: number, notificationId: number | null) => void
  tableItems: CompanyInstallerTableItemModel[]
  installersLoading: boolean
  invitesLoading: boolean
}

/**
 * Hook includes helpers to manage notifications for users in the company.
 */
export const useCompanyInstallersHelper = (companyId: number, userData?: UserData): useCompanyInstallerHelperHook => {
  const { users: installers, loading: installersLoading } = useCompanyUsers(companyId, userData?.ID)
  const [tableItems, setTableItems] = useState<CompanyInstallerTableItemModel[]>([])
  const { toggleStatus } = useCompanyErrorNotification()
  const { getUserLevelDisplayName } = useUserHelpers()
  const refresh = useTypedSelector(state => state.apiView.refresh)
  /**
   * Updates or resets the notification ID for a single user entry after the settings for a notification were changed.
   * It triggers an update the Table in <CompanyInstallers />, but not a reset (or glitch) of its content.
   */
  const updateNotificationReferenceForTableItem = useCallback(
    (userId: number, notificationId: number | null) => {
      const allUpdateItems = tableItems.map(tableItem => {
        if (tableItem.isInvite) {
          return tableItem
        }
        const userSettingsItem = tableItem.value as CompanySettingsUserDataModel

        if (userSettingsItem.ID === userId) {
          const newValue = { ...userSettingsItem, errorNotificationId: notificationId } as CompanySettingsUserDataModel
          return { ...tableItem, value: newValue }
        }

        return tableItem
      })
      setTableItems([...(allUpdateItems as CompanyInstallerTableItemModel[])])
    },
    [tableItems]
  )

  const handleChangeNotifications = useCallback(
    (value: boolean, companyId: number, userId: number, notificationId: number | null): void => {
      toggleStatus(companyId, userId, notificationId, value)
        .then(newNotificationId => updateNotificationReferenceForTableItem(userId, newNotificationId))
        .catch(console.error)
    },
    [toggleStatus, updateNotificationReferenceForTableItem]
  )

  const [invites, invitesLoading] = useFetchApiObject<CompanyInvitationModel[]>({
    url: `users/companies/${companyId}/invite`,
    doNotFetch: !userData?.ID || !companyId,
    dependencies: [refresh],
  })

  const updateInstallerTableItems = useCallback((installers: CompanySettingsUserDataModel[], invites: CompanyInvitationModel[]): void => {
    const installerTableItems = (installers || []).map(
      installer =>
        ({
          value: installer,
          email: installer.email,
          isInvite: false,
          displayName: installer.username,
          id: installer.ID,
          role: getUserLevelDisplayName(installer.level),
        } as CompanyInstallerTableItemModel)
    )
    const inviteTableItems = (invites || []).map(
      invite =>
        ({
          value: invite,
          isInvite: true,
          displayName: invite.email,
          role: '',
        } as CompanyInstallerTableItemModel)
    )
    setTableItems([...installerTableItems, ...inviteTableItems])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    updateInstallerTableItems(installers || [], invites || [])
  }, [updateInstallerTableItems, installers, invites])

  return { handleChangeNotifications, tableItems, installersLoading, invitesLoading }
}
